import { ModuleSettingsItem } from 'src/constants';

/**
 * Finds the insertion index for an item in a folder.
 * Returns the index after all existing children of the folder.
 * Returns -1 if the folder is not found.
 */
const getFolderInsertIndex = (
  moduleSettings: ModuleSettingsItem[],
  folderId: string,
): number => {
  const folderIdx = moduleSettings.findIndex((m) => m.id === folderId);
  if (folderIdx === -1) {
    return -1;
  }

  // Find the index after the last child of the folder
  let insertIdx = folderIdx + 1;
  while (
    insertIdx < moduleSettings.length &&
    moduleSettings[insertIdx]?.path === folderId
  ) {
    insertIdx++;
  }
  return insertIdx;
};

/**
 * Inserts a new item into the module settings array at the correct position.
 * If folderId is provided, the item is inserted after all existing children
 * of that folder. Otherwise, the item is appended to the end.
 */
export const insertModuleSettingsItem = (
  moduleSettings: ModuleSettingsItem[],
  item: ModuleSettingsItem,
  folderId?: string,
): ModuleSettingsItem[] => {
  if (!folderId) {
    return [...moduleSettings, item];
  }

  const insertIdx = getFolderInsertIndex(moduleSettings, folderId);
  if (insertIdx === -1) {
    // Folder not found, append to end
    return [...moduleSettings, item];
  }

  // Insert the item with the path set to the folder ID
  const itemWithPath = { ...item, path: folderId };
  const result = [...moduleSettings];
  result.splice(insertIdx, 0, itemWithPath);
  return result;
};

/**
 * Moves an existing item in the module settings array into a folder.
 * The item is removed from its current position and inserted after all
 * existing children of the folder.
 */
export const moveModuleSettingsItemToFolder = (
  moduleSettings: ModuleSettingsItem[],
  itemId: string,
  folderId: string,
): ModuleSettingsItem[] => {
  const itemIdx = moduleSettings.findIndex((m) => m.id === itemId);
  if (itemIdx === -1) {
    return moduleSettings;
  }

  const item = moduleSettings[itemIdx];
  if (!item) {
    return moduleSettings;
  }

  // Remove the item from its current position
  const result = moduleSettings.filter((_, idx) => idx !== itemIdx);

  // Find the insert index (after removal, indices may have shifted)
  const insertIdx = getFolderInsertIndex(result, folderId);
  if (insertIdx === -1) {
    // Folder not found, put item back at end
    return [...result, item];
  }

  // Insert the item with the path set to the folder ID
  const itemWithPath: ModuleSettingsItem = { ...item, path: folderId };
  result.splice(insertIdx, 0, itemWithPath);
  return result;
};
